import { useEffect } from 'react';
import { VyFormSection, VyIcon } from '@vy/ui-design-system';
import { ADVANCE_PAYMENT_TYPES, PAYMENT_TERMS, type TiersAdvancePayment } from '@vy/types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useChargeContext } from '../../charge-context';
import {
  CadenceBillingIntervalInputs,
  CalendarAlignmentSwitch,
  PaymentTermSelect,
  UserInputBillingIntervalSelect,
} from '../../modify-panel-shared/selectors';
import { FieldGrid } from '../../modify-panel-shared/layout';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

export type SchedulingSectionProps = {
  showCadence?: boolean;
  showPaymentTerm?: boolean;
};

export function SchedulingSection({ showCadence = false, showPaymentTerm = false }: SchedulingSectionProps) {
  const { itemIndex: productIndex } = useChargeContext();
  const { getValues, setValue } = useFormContext<PlanInputForm>();

  const paymentTerm = useWatch({ name: `usageProducts.${productIndex}.paymentTerm` as const });

  useEffect(() => {
    if (!showPaymentTerm) {
      return;
    }

    const advancePayment: TiersAdvancePayment = {
      advancePaymentType: ADVANCE_PAYMENT_TYPES.Tiers,
      advancePaymentConfig: {
        separateLineItems: true,
        payInAdvanceOnTierCross:
          getValues(
            `usageProducts.${productIndex}.advancePayment.advancePaymentConfig.payInAdvanceOnTierCross`,
          ) ?? false,
      },
    };

    setValue(
      `usageProducts.${productIndex}.advancePayment`,
      paymentTerm === PAYMENT_TERMS.Prepayment ? advancePayment : undefined,
    );
  }, [paymentTerm, productIndex, showPaymentTerm, setValue, getValues]);

  return (
    <VyFormSection icon={<VyIcon icon="SectionSchedule" width={20} height={20} />} title="Scheduling">
      <FieldGrid>
        {showPaymentTerm && (
          <PaymentTermSelect productType="usageProducts" productIndex={productIndex} />
        )}

        {showCadence ? (
          <CadenceBillingIntervalInputs fieldNamePrefix="usageProducts" productIndex={productIndex} syncMeterReset />
        ) : (
          <UserInputBillingIntervalSelect fieldNamePrefix="usageProducts" productIndex={productIndex} />
        )}

        <CalendarAlignmentSwitch fieldNamePrefix="usageProducts" productIndex={productIndex} />
      </FieldGrid>
    </VyFormSection>
  );
}
